import React, { useEffect, useState, useCallback } from "react";
import { Table, Button, Form, Dropdown, ButtonGroup, Spinner } from "react-bootstrap";
import useAxiosPrivate from "../../../Pages/hooks/useAxiosPrivate";
import { baseURL, STYDYMONTH, ACADIMAC } from "../../../API/API";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPercent, faSave, faFilter, faSearch } from "@fortawesome/free-solid-svg-icons";
import "./Content.css";

export default function CourseDiscounts() {
  const axiosPrivate = useAxiosPrivate();
  const [chapters, setChapters] = useState([]);
  const [academics, setAcademics] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState(null);
  
  // Filters & State
  const [selectedAcademicId, setSelectedAcademicId] = useState("-");
  const [searchQuery, setSearchQuery] = useState("");
  
  // Temporary state for editing discounts before saving
  const [discountInputs, setDiscountInputs] = useState({});

  // Fetch Academic Years for filtering
  useEffect(() => {
    async function fetchAcademics() {
      try {
        const res = await axiosPrivate.get(`${baseURL}/${ACADIMAC}`);
        if (res.data && res.data.data) {
          setAcademics(res.data.data);
        }
      } catch (err) {
        console.error("Error fetching academic years:", err);
      }
    }
    fetchAcademics();
  }, [axiosPrivate]);

  // Fetch Chapters (published & drafts)
  const fetchChapters = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch page 1 with all chapters by setting a large limit or handling pagination
      // To get all courses, we can query without pagination or pass large indexpage
      // Or call the dashboard endpoint. Let's fetch published first.
      const res = await axiosPrivate.post(`${baseURL}/${STYDYMONTH}/`, {
        Order: "id",
        OrderType: true,
        TypeStudent: selectedAcademicId,
        post: false, // published
        indexpage: 1,
      });

      const resDraft = await axiosPrivate.post(`${baseURL}/${STYDYMONTH}/`, {
        Order: "id",
        OrderType: true,
        TypeStudent: selectedAcademicId,
        post: true, // drafts
        indexpage: 1,
      });

      const allChapters = [
        ...(res.data?.data || []),
        ...(resDraft.data?.data || []),
      ];

      // Filter duplicates by ID just in case
      const uniqueChapters = Array.from(new Map(allChapters.map(item => [item.id, item])).values());

      setChapters(uniqueChapters);

      // Initialize inputs state
      const inputs = {};
      uniqueChapters.forEach((ch) => {
        inputs[ch.id] = ch.discount !== undefined ? ch.discount : 0;
      });
      setDiscountInputs(inputs);

    } catch (err) {
      console.error("Error fetching chapters:", err);
      toast.error("فشل في تحميل قائمة الكورسات");
    } finally {
      setLoading(false);
    }
  }, [axiosPrivate, selectedAcademicId]);

  useEffect(() => {
    fetchChapters();
  }, [fetchChapters]);

  const handleDiscountChange = (id, val) => {
    setDiscountInputs((prev) => ({
      ...prev,
      [id]: val,
    }));
  };

  const handleSaveDiscount = async (id, originalPrice) => {
    const discountVal = Number(discountInputs[id] || 0);

    if (isNaN(discountVal) || discountVal < 0) {
      toast.error("قيمة الخصم يجب أن تكون صفر أو قيمة موجبة");
      return;
    }

    if (discountVal > originalPrice) {
      toast.error("لا يمكن أن يكون الخصم أكبر من السعر الأصلي للكورس");
      return;
    }

    setSavingId(id);
    try {
      const formData = new FormData();
      formData.append("id", id);
      formData.append("discount", discountVal);

      await axiosPrivate.put(`${baseURL}/learning/edit-chapter`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      toast.success("تم تطبيق الخصم بنجاح!");
      fetchChapters();
    } catch (err) {
      console.error("Error updating discount:", err);
      toast.error("حدث خطأ أثناء حفظ الخصم");
    } finally {
      setSavingId(null);
    }
  };

  // Filter local chapters based on search query
  const filteredChapters = chapters.filter((ch) =>
    (ch.chapterName || ch.name || "").toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="p-4 content" dir="rtl">
      <div className="d-flex align-items-center justify-content-between mb-5 flex-wrap gap-3">
        <h2 className="fw-bold m-0 d-flex align-items-center gap-3">
          <FontAwesomeIcon icon={faPercent} className="text-primary" />
          <span>إدارة خصومات الكورسات (الفصول الدراسية)</span>
        </h2>
        <Button onClick={fetchChapters} className="custom-brand-btn-dhash" variant="outline-secondary">
          تحديث البيانات
        </Button>
      </div>

      {/* Filter and Search controls */}
      <div className="d-flex mb-4 gap-3 flex-wrap align-items-center bg-white p-3 rounded shadow-sm">
        <div className="d-flex align-items-center gap-2">
          <FontAwesomeIcon icon={faFilter} className="text-muted" />
          <span className="small text-muted fw-bold">تصفية حسب:</span>
        </div>

        <Dropdown as={ButtonGroup}>
          <Button className="custom-brand-btn-dhash" variant="secondary">
            {selectedAcademicId === "-" ? "كل السنوات الدراسية" : academics.find(a => a.id === selectedAcademicId)?.name || "السنة الدراسية"}
          </Button>
          <Dropdown.Toggle split variant="secondary" id="dropdown-academic-filter" style={{ backgroundColor: "var(--brand-200)", boxShadow: "0 3px 0px rgb(3, 42, 61)" }} />
          <Dropdown.Menu>
            <Dropdown.Item onClick={() => setSelectedAcademicId("-")}>كل السنوات الدراسية</Dropdown.Item>
            {academics.map((acd) => (
              <Dropdown.Item key={acd.id} onClick={() => setSelectedAcademicId(acd.id)}>
                {acd.name}
              </Dropdown.Item>
            ))}
          </Dropdown.Menu>
        </Dropdown>

        <div className="position-relative flex-grow-1" style={{ maxWidth: "400px" }}>
          <Form.Control
            type="text"
            placeholder="بحث باسم الكورس..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pe-5"
            style={{ borderRadius: "20px", border: "1.5px solid #cbd5e1" }}
          />
          <FontAwesomeIcon icon={faSearch} className="position-absolute text-muted" style={{ left: "auto", right: "20px", top: "50%", transform: "translateY(-50%)" }} />
        </div>
      </div>

      {/* Main Content Table */}
      {loading && chapters.length === 0 ? (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-2 text-muted">جاري تحميل الكورسات...</p>
        </div>
      ) : (
        <div className="table-responsive bg-white rounded shadow-sm p-3">
          <Table striped bordered hover className="align-middle text-center m-0">
            <thead style={{ backgroundColor: "var(--brand-200)", color: "#fff" }}>
              <tr>
                <th>اسم الكورس</th>
                <th>السنة الدراسية</th>
                <th>السعر الأصلي</th>
                <th style={{ width: "220px" }}>الخصم (جنيه)</th>
                <th>السعر بعد الخصم</th>
                <th>الحالة</th>
                <th>إجراءات</th>
              </tr>
            </thead>
            <tbody>
              {filteredChapters.length === 0 ? (
                <tr>
                  <td colSpan="7" className="text-center py-4 text-muted">
                    لا توجد كورسات تطابق معايير البحث والفلترة.
                  </td>
                </tr>
              ) : (
                filteredChapters.map((row) => {
                  const originalPrice = Number(row.price || row.chapterPrice || 0);
                  const discountVal = Number(discountInputs[row.id] || 0);
                  const finalPrice = Math.max(originalPrice - discountVal, 0);

                  return (
                    <tr key={row.id}>
                      <td className="fw-bold">{row.chapterName || row.name}</td>
                      <td>{row.academicYear || "—"}</td>
                      <td className="text-muted">{originalPrice.toFixed(2)} ج.م</td>
                      <td>
                        <Form.Group className="m-0">
                          <Form.Control
                            type="number"
                            min="0"
                            step="0.01"
                            value={discountInputs[row.id] !== undefined ? discountInputs[row.id] : ""}
                            onChange={(e) => handleDiscountChange(row.id, e.target.value)}
                            placeholder="0.00"
                            className="text-center"
                            style={{ maxWidth: "150px", margin: "0 auto", border: "1px solid #cbd5e1" }}
                          />
                        </Form.Group>
                      </td>
                      <td>
                        <span className={`fw-bold fs-5 ${discountVal > 0 ? "text-danger" : "text-dark"}`}>
                          {finalPrice.toFixed(2)} ج.م
                        </span>
                        {discountVal > 0 && (
                          <div className="small text-muted text-decoration-line-through">
                            {originalPrice.toFixed(2)} ج.م
                          </div>
                        )}
                      </td>
                      <td>
                        {row.is_draft ? (
                          <span className="badge bg-secondary">مسودة</span>
                        ) : (
                          <span className="badge bg-success">منشور</span>
                        )}
                      </td>
                      <td>
                        <Button
                          variant={discountVal > 0 ? "danger" : "primary"}
                          size="sm"
                          disabled={savingId === row.id}
                          onClick={() => handleSaveDiscount(row.id, originalPrice)}
                          className="d-inline-flex align-items-center gap-2 px-3 py-2 rounded-pill"
                          style={{
                            minWidth: "120px",
                            justifyContent: "center",
                            boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
                          }}
                        >
                          {savingId === row.id ? (
                            <Spinner size="sm" animation="border" />
                          ) : (
                            <>
                              <FontAwesomeIcon icon={faSave} />
                              <span>تطبيق الخصم</span>
                            </>
                          )}
                        </Button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </Table>
        </div>
      )}
    </div>
  );
}
